import { clsx } from '../../../../ds/clsx';
import type { MatrixRole, ValueOption } from './api';
import styles from './PermissionMatrix.module.css';

export interface MatrixCellView {
  roleId: string;
  value: string;
  label: string;
  /** Renders a select of `options` instead of the plain value. */
  editable: boolean;
  /** Accent chip: a deviation from the template (Admin 02) or a change that is not saved yet. */
  highlighted: boolean;
  /** Open request of a sales lead (Admin 02): the requested value, shown after the cell's value. */
  pendingLabel?: string | null | undefined;
  /** Why the cell cannot be changed (`actions.save.reason`), as the select's title. */
  lockedReason?: string | null | undefined;
}

export interface MatrixRowView {
  key: string;
  label: string;
  sensitive: boolean;
  options: ValueOption[];
  cells: MatrixCellView[];
}

export interface PermissionMatrixProps {
  roles: MatrixRole[];
  categories: { label: string; rows: MatrixRowView[] }[];
  onChange: (roleId: string, key: string, value: string) => void;
}

/** Up to seven role columns fill the card as drawn; more scroll horizontally behind the sticky first column. */
const FITTING_ROLES = 7;

/**
 * The role × permission matrix of Admin 01/02: grid `minmax(0,1.5fr) + N × minmax(0,1fr)`, sticky head, category
 * rows, the value words of the catalog („ja", „nein", „Führungsspanne" …), „heikel" after sensitive labels. An
 * editable cell is a select of the allowed values, styled as the value itself.
 */
export function PermissionMatrix({ roles, categories, onChange }: PermissionMatrixProps) {
  const wide = roles.length > FITTING_ROLES;
  const template = wide
    ? `minmax(240px, 1.5fr) repeat(${roles.length}, minmax(130px, 1fr))`
    : `minmax(0, 1.5fr) repeat(${roles.length}, minmax(0, 1fr))`;
  const roleName = new Map(roles.map((role) => [role.id, role.name]));

  return (
    <div className={clsx(styles.card, wide && styles.wide)}>
      <div className={clsx(wide && styles.scroll)} role="table" aria-label="Rechte je Rolle">
        <div
          className={clsx(styles.grid, styles.head)}
          style={{ gridTemplateColumns: template, minWidth: wide ? 'max-content' : undefined }}
          role="row"
        >
          <span className={clsx(styles.headCell, styles.headFirst)} role="columnheader">
            Recht
          </span>
          {roles.map((role) => (
            <span key={role.id} className={styles.headCell} role="columnheader" title={role.name}>
              {role.name}
            </span>
          ))}
        </div>
        {categories.map((category) => (
          <div key={category.label} role="rowgroup" aria-label={category.label}>
            <div className={styles.category} role="row">
              <span role="rowheader">{category.label}</span>
            </div>
            {category.rows.map((row) => (
              <div
                key={row.key}
                className={clsx(styles.grid, styles.row)}
                style={{ gridTemplateColumns: template, minWidth: wide ? 'max-content' : undefined }}
                role="row"
              >
                <span className={styles.label} role="rowheader">
                  {row.label}
                  {row.sensitive ? <span className={styles.sensitive}>heikel</span> : null}
                </span>
                {row.cells.map((cell) => {
                  const name = `${row.label} · ${roleName.get(cell.roleId) ?? ''}`;
                  return (
                    <span
                      key={cell.roleId}
                      role="cell"
                      className={clsx(
                        styles.cell,
                        cell.value === 'yes'
                          ? styles.valueYes
                          : cell.value === 'no' || cell.value === 'none'
                            ? styles.valueNo
                            : undefined,
                        cell.highlighted && styles.chip,
                      )}
                    >
                      {cell.editable ? (
                        <select
                          aria-label={name}
                          className={styles.select}
                          value={cell.value}
                          title={cell.lockedReason ?? undefined}
                          onChange={(event) => onChange(cell.roleId, row.key, event.target.value)}
                        >
                          {row.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span title={cell.lockedReason ?? undefined}>{cell.label}</span>
                      )}
                      {cell.pendingLabel ? (
                        <span className={styles.pending}>Antrag: {cell.pendingLabel}</span>
                      ) : null}
                    </span>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
